import { useState, useEffect, useRef, forwardRef, useImperativeHandle, } from 'react';
import router, { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'framer-motion';

import { useQuery } from '@tanstack/react-query';
import { defaultColor } from '@components/Presentation/constants';

import useLoadingTimeout from '@lib/hooks/useLoadingTimeout';
import Loader from '@atoms/Atom/Loader';
import Image from '@atoms/Image';

import LeaderboardItem from './LeaderboardItem';

import { getQuizScoresPaginated } from '@services/youtube-platform.service';

import useNotificationTimer from '@lib/hooks/useNotificationTimer ';
import useFonts from '@lib/hooks/useFonts';
import useAnimation from '@lib/hooks/useAnimation';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
import ImageLoader from '@atoms/new/imageLoader';

const LeaderboardAnalytics = forwardRef(({
  analyticsData,
}, ref) => {
  const getStyle = () => ({
    cursor: 'default',
  });
  const { getFontStyle } = useFonts();
  const [animatedCount, setAnimatedCount] = useState([]);
  const [animatedRelativePercentage, setAnimatedRelativePercentage] = useState([]);

  const { fontFamily } = getFontStyle(analyticsData?.settings?.FontStyle || 'Sans-Serif');

  useAnimation(analyticsData?.rows, 'totalScore', setAnimatedCount, analyticsData?.id);
  useAnimation(analyticsData?.rows, 'totalScore', setAnimatedRelativePercentage, analyticsData?.id);
  const quizImage = analyticsData?.settings?.quizImageSrc;
  const quizImagePath = `${process.env.NEXT_PUBLIC_BUCKET_URL}${quizImage}`;

  return <div className='flex w-full h-full relative' >
    <div
      id='scrollableDiv'
      style={{
        ...getStyle(),
        fontFamily,
        backgroundImage: quizImagePath ? `url(${quizImagePath})` : undefined,
      }}
      className={'flex-1 scrollable-container max-w-6xl mx-auto flex flex-col pb-4 !overflow-y-auto relative p-4'}
    >
      <div className='absolute top-0 left-0 bottom-0 right-0 z-0'>
      </div>
      {/* <ResultHeader title={analyticsData?.title} type={analyticsData?.type} textColor={analyticsData?.color.leaderboardHeadingColor} showType={false} /> */}
      <AnimatePresence>
        <motion.div layout className="space-y-4">
          <LeaderboardItem leaderboard={analyticsData?.rows} animatedCount={animatedCount} animatedRelativePercentage={animatedRelativePercentage} colors={analyticsData?.color} isLeaderboard />
        </motion.div>
      </AnimatePresence>
    </div>
  </div>;
});

const Leaderboard = forwardRef(({
  showingLeaderboard = false,
  streamId,
  currentRosFeature,
  setShowNotification,
  isSandbox = false,
  isAnalytics = false,
  analyticsData
}, ref) => {
  if (isAnalytics) {
    return <LeaderboardAnalytics analyticsData={analyticsData} ref={ref} />;
  }

  const [leaderboardScores, setLeaderboardScores] = useState([]);
  const [animatedCount, setAnimatedCount] = useState([]);
  const [animatedRelativePercentage, setAnimatedRelativePercentage] = useState([]);
  const [colors, setColors] = useState(currentRosFeature?.color || defaultColor);
  const [isLoading, setIsLoading] = useLoadingTimeout(30000);

  const isFirstCallRef = useRef(true);

  const joinType = useRouter().query?.joinType;

  useNotificationTimer(currentRosFeature, leaderboardScores, setShowNotification);

  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(currentRosFeature?.setting?.FontStyle || 'Sans-Serif');

  const leaderboardData = useQuery({
    queryKey: [
      'quiz_leaderboard',
      streamId,
      currentRosFeature?.id,
      currentRosFeature?.setting?.leaderboardShowWinners,
      currentRosFeature?.question_number
    ],
    queryFn: () =>
      getQuizScoresPaginated(streamId, {
        filter: currentRosFeature?.setting?.leaderboardShowWinners,
        questionNumber: currentRosFeature?.question_number
      }),
    enabled: showingLeaderboard
  });

  useEffect(() => {
    if (leaderboardData?.data?.status && leaderboardData?.data?.entity?.rows?.length > 0) {
      if (isFirstCallRef.current) {
        setIsLoading(true);
      }

      setLeaderboardScores(leaderboardData.data.entity.rows);

      if (isFirstCallRef.current) {
        setIsLoading(false);
        isFirstCallRef.current = false;
      }
    }
  }, [leaderboardData]);

  useAnimation(leaderboardScores, 'totalScore', setAnimatedCount, currentRosFeature?.id);
  useAnimation(leaderboardScores, 'totalScore', setAnimatedRelativePercentage, currentRosFeature?.id);

  useEffect(() => {
    setAnimatedCount([]);
    setAnimatedRelativePercentage([]);
  }, [currentRosFeature?.id]);

  useEffect(() => {
    if (currentRosFeature?.color) {
      setColors(currentRosFeature.color);
    }
  }, [currentRosFeature?.color]);

  useEffect(() => {
    if (currentRosFeature?.setting) {
      leaderboardData.refetch();
    }
  }
  , [currentRosFeature?.setting]);

  const stopCurrentFeature = () => {
    router.push({ pathname: `/streams/${streamId}/ros`, query: joinType && { joinType } });
  };

  useImperativeHandle(ref, () => ({ stopCurrentFeature }));

  const getStyle = () => ({
    cursor: 'default',
    ...(isSandbox && { height: 'calc(100vh - 400px)' }),
  });

  const computedFontSize = useFontSizeCalculator(currentRosFeature?.setting?.leaderboardFontSize ? Number(currentRosFeature?.setting?.leaderboardFontSize) : 1.2, 1.8, 'rem');

  return (
    <div className='flex w-full h-full relative' >
      <div
        id='scrollableDiv'
        style={{ ...getStyle(), fontFamily }}
        className={'flex-1 scrollable-container max-w-6xl mx-auto flex flex-col pb-4 !overflow-y-auto'}
      >
        {isLoading ? <Loader showMessages={false} />
          : <> {leaderboardScores.length > 0 ?
            <AnimatePresence>
              <motion.div layout className="space-y-4">
                <LeaderboardItem leaderboard={leaderboardScores} animatedCount={animatedCount} animatedRelativePercentage={animatedRelativePercentage} colors={colors} fontSize={computedFontSize} isLeaderboard />
              </motion.div>
            </AnimatePresence>
            : <div className='absolute m-0 top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2'>
              <div className='flex flex-col items-center'>
                <Image src='/images/chat.svg' />
                <div className='mt-8'>
                  <div className='flex justify-center items-center'>
                    <span className='font-medium	text-white text-center text-lg leading-6'>Nobody has commented</span><br />
                  </div>
                  <div className='flex justify-center items-center'>
                    <span className='font-medium	text-white text-center text-lg leading-6'>yet in the chat</span>
                  </div>
                </div>
              </div>
            </div>
          }
          </>}
      </div>
    </div>
  );
});

export default Leaderboard;
